/**
 * Empty the tenant before loading it again.
 *
 * Three decisions, all about not doing this by accident:
 *
 * **It says what it will delete, in numbers, before offering to.** "Réinitialiser"
 * alone is a word; "32 086 articles, 10 548 clients" is a decision somebody
 * can take or refuse.
 *
 * **The confirmation is the tenant's identifier, typed.** A checkbox is ticked
 * by reflex; a name has to be read — which is the moment somebody notices they
 * are on the client's installation rather than the demonstration.
 *
 * **It is invisible where it does not exist.** Without the permission the panel
 * renders nothing; with it, a 404 from the preview means the deployment has not
 * enabled the capability, and it renders nothing either. A disabled button
 * explaining a server setting would only invite somebody to go and change it.
 */

import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { quantity } from "@hardauto/core-i18n";
import { Alert, Button, Card, Dialog, Input, Stat, StatGrid, useToast } from "@hardauto/core-ui";

import { DataResetApi } from "../api";
import { NAMESPACE } from "../i18n";

/** The tables an operator recognises, shown as figures above the detail. */
const HEADLINE = ["articles", "clients", "orders", "invoices"] as const;

export function DataResetPanel({
  client,
  onReset,
}: {
  client: ApiClient;
  /** Called after a reset, so the rest of the screen can read again. */
  onReset?: () => void;
}) {
  // Checked before anything is fetched: a user without the permission must
  // not even cause the request.
  const mayReset = useCan("maintenance.data.reset");
  return mayReset ? <ResetCard client={client} onReset={onReset} /> : null;
}

function ResetCard({ client, onReset }: { client: ApiClient; onReset?: (() => void) | undefined }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new DataResetApi(client), [client]);
  const toast = useToast();
  const action = useAction();

  const previewFetcher = useCallback(() => api.preview(), [api]);
  const preview = useResource(previewFetcher);

  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");

  // Off on this deployment, or the permission was withdrawn since sign-in.
  if (preview.error instanceof ApiError && [403, 404].includes(preview.error.status)) {
    return null;
  }
  if (preview.failed) {
    return (
      <Card title={t("reset.title")}>
        <Alert tone="danger" title={t("error.load")}>
          <Button size="sm" onClick={preview.reload}>
            {t("error.retry")}
          </Button>
        </Alert>
      </Card>
    );
  }
  const summary = preview.data;
  if (!summary) return null;

  const count = (value: number) => quantity(String(value), i18n.language);
  const rowsOf = (table: string) => summary.tables.find((row) => row.table === table)?.rows ?? 0;
  const matches = typed.trim() === summary.tenant_slug;
  // Already empty: the contract's entities are not counted, because the reset
  // would only write them back. Offering the button would offer a no-op.
  const empty = summary.total_rows === 0 && summary.customer_accounts === 0;

  function close() {
    setConfirming(false);
    setTyped("");
  }

  function run() {
    void action.run("reset", async () => {
      try {
        // What the person typed, not the slug the preview returned: the
        // server's check is then a check of the human, not of this screen.
        const done = await api.reset(typed.trim());
        close();
        toast.show(t("reset.done", { rows: count(done.total_rows) }), { tone: "success" });
        preview.reload();
        onReset?.();
      } catch (error) {
        toast.show(error instanceof Error ? error.message : t("error.title"), { tone: "danger" });
      }
    });
  }

  return (
    <Card title={t("reset.title")} description={t("reset.intro")}>
      <Alert tone="danger" title={t("reset.irreversible")}>
        {t("reset.kept")}
      </Alert>

      <div className="hardauto-exchange-result">
        <StatGrid label={t("reset.summary")}>
          <Stat label={t("reset.rows")} value={count(summary.total_rows)} tone="danger" />
          {HEADLINE.map((table) => (
            <Stat key={table} label={t(`reset.table.${table}`)} value={count(rowsOf(table))} />
          ))}
          <Stat
            label={t("reset.customerAccounts")}
            value={count(summary.customer_accounts)}
            tone={summary.customer_accounts > 0 ? "warning" : "neutral"}
          />
          <Stat
            label={t("reset.keptAccounts")}
            value={count(summary.kept_accounts)}
            tone="success"
          />
        </StatGrid>

        {summary.tables.length > 0 ? (
          <details>
            <summary>{t("reset.detail", { count: summary.tables.length })}</summary>
            <dl className="hardauto-exchange-facts">
              {summary.tables.map((row) => (
                <Fragment key={row.table}>
                  <dt>
                    <code className="hardauto-code">{row.table}</code>
                  </dt>
                  <dd>{count(row.rows)}</dd>
                </Fragment>
              ))}
            </dl>
          </details>
        ) : null}

        {empty ? (
          <Alert tone="info" title={t("reset.empty")} />
        ) : (
          <div>
            <Button variant="danger" onClick={() => setConfirming(true)}>
              {t("reset.open")}
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={confirming}
        onClose={close}
        title={t("reset.confirmTitle", { name: summary.tenant_name })}
        description={t("reset.confirmBody", {
          rows: count(summary.total_rows),
          accounts: count(summary.customer_accounts),
        })}
        closeLabel={t("reset.cancel")}
        footer={
          <>
            <Button onClick={close}>{t("reset.cancel")}</Button>
            <Button
              variant="danger"
              disabled={!matches}
              busy={action.isBusy("reset")}
              busyLabel={t("reset.running")}
              onClick={run}
            >
              {t("reset.confirm")}
            </Button>
          </>
        }
      >
        <Field label={t("reset.typeToConfirm", { slug: summary.tenant_slug })}>
          {(props) => (
            <Input
              {...props}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </Card>
  );
}
